import { Box, Stack, styled, Typography, TypographyProps } from '@mui/joy';
import { Theme } from '@mui/joy/styles';
import { cloneElement } from 'react';

import { containsHtmlTags, isString, parseString } from 'core/utils';
import { Color, Direction, Level } from 'theme/enums';

import TextEllipsis from './TextEllipsis';

interface IProps extends Omit<TypographyProps, 'color' | 'lineHeight'> {
  text: string | JSX.Element;
  label?: string | JSX.Element;
  color?: Color;
  isSubText?: boolean;
  italic?: boolean;
  bold?: boolean;
  upperCase?: boolean;
  maxRows?: number;
  textEllipsis?: boolean;
  onClick?: () => void;
  decorator?: JSX.Element;
  endDecorator?: JSX.Element;
}

interface IContent extends Omit<TypographyProps, 'color'> {
  color?: Color;
  isSubText?: boolean;
  italic?: boolean;
  bold?: boolean;
  upperCase?: boolean;
}

const getTextColor = (theme: Theme, color?: Color, isSubText?: boolean) => {
  const colorValue = isSubText && !color ? Color.NEUTRAL : color;

  switch (colorValue) {
    case Color.PRIMARY:
      return theme.palette.primary.softColor;
    case Color.SUCCESS:
      return theme.palette.success.softColor;
    case Color.DANGER:
      return theme.palette.danger.softColor;
    case Color.WARNING:
      return theme.palette.warning.softColor;
    case Color.NEUTRAL:
      return theme.palette.neutral[600];
    default:
      return theme.palette.neutral[800];
  }
};

const propsToSkip = ['isSubText', 'italic', 'bold', 'upperCase'];
const shouldForwardProp = (prop: string) => !propsToSkip.includes(prop);

const Content = styled(Box, { shouldForwardProp })<IContent>(
  ({ theme, color, isSubText, italic, bold, upperCase, onClick }) => ({
    display: 'flex',
    justifyContent: 'inherit',
    alignItems: 'inherit',
    gap: '4px',
    fontSize: isSubText ? 12 : 14,
    color: getTextColor(theme, color, isSubText),
    fontStyle: italic ? 'italic' : 'normal',
    fontWeight: bold ? 600 : 400,
    textTransform: upperCase ? 'uppercase' : 'none',
    cursor: onClick ? 'pointer' : 'default',
  })
);

const TypographyNoWrap = styled(Typography)({
  whiteSpace: 'nowrap',
});

const TextLine = ({
  text,
  label,
  isSubText,
  bold,
  italic,
  upperCase,
  maxRows,
  textEllipsis = false,
  onClick,
  decorator,
  endDecorator,
  ...rest
}: IProps) => {
  const level = isSubText ? Level.BODY_XS : Level.BODY_SM;

  const labelContent = isString(label) ? `${label}:` : label;

  const renderContent = () => {
    if (textEllipsis) {
      return <TextEllipsis level={level} {...rest} content={text} maxRows={maxRows} />;
    }

    if (isString(text)) {
      const parsedText = parseString(text as string);

      if (containsHtmlTags(text as string)) {
        return <Box sx={{ textWrap: 'wrap', ...(rest?.sx || {}) }}>{parsedText}</Box>;
      }

      return (
        <Typography
          level={level}
          {...rest}
          sx={{ textWrap: 'wrap', ...(rest?.sx || {}) }}
          startDecorator={decorator}
          endDecorator={endDecorator}
        >
          {parsedText}
        </Typography>
      );
    }

    return (
      <Stack direction={Direction.ROW} gap={0.5}>
        {decorator && cloneElement(decorator, { style: { flexShrink: 0 } })}
        {text}
      </Stack>
    );
  };

  return (
    <Content
      isSubText={isSubText}
      italic={italic}
      bold={bold}
      upperCase={upperCase}
      onClick={onClick}
      {...rest}
    >
      {labelContent && isString(labelContent) && (
        <TypographyNoWrap level={level} color={rest?.color || Color.NEUTRAL} {...rest}>
          {labelContent}
        </TypographyNoWrap>
      )}

      {labelContent && !isString(labelContent) && labelContent}

      {renderContent()}
    </Content>
  );
};

export default TextLine;
